import Cookies from 'js-cookie';

import type { AnimationBuilder } from '@ionic/react';
import { isPlatform } from '@ionic/react';
import { createAnimation } from '@ionic/react';

import { environment } from '../environment/environment';

export const isNative = () => (isPlatform('ios') || isPlatform('android')) && !isPlatform('mobileweb');

export const STORAGE = isNative() ? localStorage : sessionStorage;

export const getDidOnboarding = () => STORAGE.getItem('didOnboarding') === 'true';
export const setDidOnboarding = () => STORAGE.setItem('didOnboarding', 'true');

export const Org = {
  Saskatchewan: 'sk',
  OneidaTS: 'onwts',
  NisgaaValley: 'nvha',
  Milwaukee: 'mlw',

  // test orgs
  Ontario: 'sk2',
  Wisconsin: 'sk3',
  Check: 'check',
} as const;

export type Org = (typeof Org)[keyof typeof Org];

export const AppCode = {
  TalkingStick: 'ts',
};

export type AppCode = (typeof AppCode)[keyof typeof AppCode];

export const getOrgCode = (): Org => environment.orgCode as Org;

export const getAppCode = (): AppCode => environment.appCode as AppCode;

export const appName = 'Talking Stick';
export const infoEmail = 'info@talkingsrick.app';
export const appLogo = {
  src: 'assets/imgs/ts-logo-no-text.svg',
  alt: 'Talking Stick logo.',
};

export const getBgStyle = () => {
  const bgStyle = {
    '--leps-background': 'no-repeat top / cover url(/assets/imgs/hummingbird.webp)',
    '--sp-background': 'no-repeat top / cover url(/assets/imgs/special-teams-header.webp)',
    '--fa-background': 'no-repeat top / cover url(/assets/imgs/fa_header.svg)',
    '--header-bg-opacity': '0.3',
  };

  switch (getOrgCode()) {
    case Org.OneidaTS:
      bgStyle['--leps-background'] = 'no-repeat top / cover url(/assets/imgs/oneida-header.svg)';
      bgStyle['--sp-background'] = 'no-repeat top / cover url(/assets/imgs/oneida-header.svg)';
      bgStyle['--fa-background'] = 'no-repeat top / cover url(/assets/imgs/oneida-header.svg)';
      break;
    case Org.Ontario:
      bgStyle['--leps-background'] = 'no-repeat top / cover url(/assets/imgs/bird.svg)';
      bgStyle['--sp-background'] = 'no-repeat top / cover url(/assets/imgs/bird.svg)';
      bgStyle['--fa-background'] = 'no-repeat top / cover url(/assets/imgs/bird.svg)';
      break;
    case Org.Wisconsin:
      bgStyle['--leps-background'] = 'no-repeat top / cover url(/assets/imgs/snow-blowing.svg)';
      bgStyle['--sp-background'] = 'no-repeat top / cover url(/assets/imgs/snow-blowing.svg)';
      bgStyle['--fa-background'] = 'no-repeat top / cover url(/assets/imgs/snow-blowing.svg)';
      break;
    case Org.Milwaukee: {
      const milwaukeeHeader = window.matchMedia('(min-width: 768px)').matches
        ? 'milwaukee-header-wide.webp'
        : 'milwaukee-header.webp';
      bgStyle['--leps-background'] = `no-repeat top / cover url(/assets/imgs/${milwaukeeHeader})`;
      bgStyle['--sp-background'] = `no-repeat top / cover url(/assets/imgs/${milwaukeeHeader})`;
      bgStyle['--fa-background'] = `no-repeat top / cover url(/assets/imgs/${milwaukeeHeader})`;
      bgStyle['--header-bg-opacity'] = '0.6';
      break;
    }
  }

  return bgStyle;
};

export const getAnimationBuilder: AnimationBuilder = (baseEl, ops) => {
  const enteringAnimation = createAnimation()
    .addElement(ops.enteringEl)
    .fill('forwards')
    .duration(150)
    .fromTo('opacity', '0', '1');
  const leavingAnimation = createAnimation().addElement(ops.leavingEl).fill('forwards').fromTo('opacity', '1', '0');
  const animation = createAnimation().addElement(baseEl).addAnimation([enteringAnimation, leavingAnimation]);
  return animation;
};

export const getViewInternalFACommunities = () => STORAGE.getItem('view_internal_fa_communities') === 'true';
export const setViewInternalFACommunities = () => STORAGE.setItem('view_internal_fa_communities', 'true');
export const removeViewInternalFACommunities = () => STORAGE.removeItem('view_internal_fa_communities');

export const getStoredDocTermsShown = () => STORAGE.getItem('doc_terms_shown') === 'true';
export const setStoredDocTermsShown = () => STORAGE.setItem('doc_terms_shown', 'true');
export const getStoredDocPrivacyShown = () => STORAGE.getItem('doc_privacy_shown') === 'true';
export const setStoredDocPrivacyShown = () => STORAGE.setItem('doc_privacy_shown', 'true');

export const getStoredSafeExitWizardShown = () => STORAGE.getItem('safe_exit_wizard_shown') === 'true';
export const setStoredSafeExitWizardShown = () => STORAGE.setItem('safe_exit_wizard_shown', 'true');
export const getStoredSortWizardShown = () => STORAGE.getItem('sort_wizard_shown') === 'true';
export const setStoredSortWizardShown = () => STORAGE.setItem('sort_wizard_shown', 'true');

export const removeCookies = () => {
  const [_subdomain, rootDomain, topLevelDomain] = location.hostname.split('.');
  const CookiesAPI = Cookies.withAttributes({ path: '/', domain: `.${rootDomain}.${topLevelDomain}` });
  const names = Object.keys(CookiesAPI.get());
  for (const name of names) {
    CookiesAPI.remove(name);
  }
};
